import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, Button, Grid, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Button as SMButton, Loader, Page, PageTitleWithButton, Pagination, Table } from '../../../components';
import { MobileTable } from '../../../components/table/MobileTable';
import { getFramers, deleteFramer, getFramerDetails, getFramerQueryData } from '../../../fetch';
import { IFramer, IFramerDetail } from '../../../models/framers';
import { FramerFilters } from './FramerFilters';
import { FramerModal } from './FramerModal';

export const ManageFramers = () => {
  const classes = useStyles();

  const [framers, setFramers] = useState<IFramer[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingFramers, setIsLoadingFramers] = useState(true);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedFramer, setSelectedFramer] = useState(null);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const [businessNameSearchValue, setBusinessNameSearchValue] = useState<string>('');
  const [contactNameSearchValue, setContactNameSearchValue] = useState<string>('');

  const [territories] = useState<{ territory: string }[]>([]);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');

  const [neighborhoods] = useState<{ neighborhood: string }[]>([]);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');

  const [isLoadingActiveBuildCounts, setLoadingActiveBuildCounts] = useState<boolean>(true);
  const [activeBuildCounts, setActiveBuildCounts] = useState<{ activeBuildCount: number }[]>([]);
  const [selectedActiveBuildCount, setSelectedActiveBuildCount] = useState<number>(Number(''));

  const [filters, setFilters] = useState<{
    businessName?: string;
    contactName?: string;
    contactNumber?: string;
    activeBuildCountStart?: number;
    activeBuildCountEnd?: number;
    territoryId?: number | string;
    neighborhoodId?: string | number;
  }>({});

  const fetchFramers = async () => {
    try {
      setIsLoadingFramers(true);
      const res = await getFramers({
        page: page + 1,
        perPage,
        sortBy: 'Name',
        ...filters
      });
      setRecordCount(res.totalRecordCount);
      setFramers(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingFramers(false);
    }
  };

  const fetchFramerQueryData = async () => {
    setLoadingActiveBuildCounts(true);
    try {
      const res = await getFramerQueryData();
      setActiveBuildCounts(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingActiveBuildCounts(false);
    }
  };

  useEffect(() => {
    fetchFramerQueryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFramers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters]);

  const handleEditFramer = async framer => {
    setIsModalOpen(true);
    setIsModalLoading(true);
    try {
      const res = await getFramerDetails(framer.framerId);
      setSelectedFramer(res);
      setIsModalLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddFramer = () => {
    setSelectedFramer(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedFramer(null);
    setIsModalOpen(false);
  };

  const handleFramerDelete = async (framerId: number) => {
    const result = window.confirm('Are you sure you want to delete this Framer?');
    if (result) {
      setDeleting(true);
      try {
        await deleteFramer(framerId);
        fetchFramers();
        enqueueSnackbar('Framer removed!', {
          variant: 'success'
        });
      } catch (error) {
          enqueueSnackbar(error?.Detail ?? 'Error deleting Framer.', {
            variant: 'error'
          });
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = [
    {
      Header: 'Business Name',
      accessor: 'businessName',
    },
    {
      Header: 'Contact Name',
      accessor: 'contactName',
    },
    {
      Header: 'Territories',
      accessor: 'territories',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IFramer } };
      }) => {
        return (
          <>
            {original.territories?.length > 0
              ? original.territories?.map(territory => territory)?.join(', ')
              : ''}
          </>
        );
      }
    },
    {
      Header: 'Neighborhoods',
      accessor: 'neighborhoods',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IFramer } };
      }) => {
        return (
          <>
            {original.neighborhoods?.length > 0
              ? original.neighborhoods?.map(neighborhood => neighborhood)?.join(', ')
              : ''}
          </>
        );
      }
    },
    {
      Header: 'Contact Numer',
      accessor: 'contactPhone',
      sort: false
    },
    {
      Header: 'Active Build Count',
      accessor: 'activeBuildCount',
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: any } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton id="edit-framer" type="button" color="primary" startIcon={<Edit />} onClick={() => handleEditFramer(original)}>
              Edit
            </SMButton>
            <SMButton
              id="delete"
              disabled={original.activeBuildCount > 0 ? true : false}
              className={classes.deleteButton}
              startIcon={<Delete />}
              onClick={() => {
                handleFramerDelete(original.framerId);
              }}
            >
              Delete
            </SMButton>
          </div>
        );
      }
    }
  ];

  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <Page title="Manage Framers">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Framers'
          }}
          button={
            <Box display="flex">

                <Button
                  id="create-framer"
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  className={classes.addFramerButton}
                  onClick={() => handleAddFramer()}
                >
                  Add Framer
                </Button>

            </Box>
          }
        />
        <Grid container spacing={1} alignItems="center" className={classes.tableWrapper}>
          <Grid item xs={12}>
            <FramerFilters
              isLoading={isLoadingFramers || isLoadingActiveBuildCounts}
              territories={territories}
              neighborhoods={neighborhoods}
              activeBuildCounts={activeBuildCounts}
              handleBusinessNameSearch={(clearSearch?: boolean) => {
                setPage(0);
                setFilters({
                  ...filters,
                  businessName: businessNameSearchValue,
                  contactName: contactNameSearchValue,
                  territoryId: selectedTerritoryId,
                  neighborhoodId: selectedNeighborhoodId,
                  activeBuildCountStart: selectedActiveBuildCount,
                  activeBuildCountEnd: selectedActiveBuildCount
                });
      
                if (clearSearch) {
                  setBusinessNameSearchValue('');
                }
              }}
              handleContactNameSearch={(clearSearch?: boolean) => {
                setPage(0);
                setFilters({
                  ...filters,
                  businessName: businessNameSearchValue,
                  contactName: contactNameSearchValue,
                  territoryId: selectedTerritoryId,
                  neighborhoodId: selectedNeighborhoodId,
                  activeBuildCountStart: selectedActiveBuildCount,
                  activeBuildCountEnd: selectedActiveBuildCount
                });
      
                if (clearSearch) {
                  setContactNameSearchValue('');
                }
              }}
              applyFilters={(clearFilters?: boolean) => {
                if (clearFilters) {
                  setFilters({
                    businessName: '',
                    contactName: '',
                    territoryId: '',
                    neighborhoodId: '',
                    activeBuildCountStart: Number(''),
                    activeBuildCountEnd: Number('')
                  });
                } else {
                  setPage(0);
                  setFilters({
                    ...filters,
                    businessName: businessNameSearchValue,
                    contactName: contactNameSearchValue,
                    territoryId: selectedTerritoryId,
                    neighborhoodId: selectedNeighborhoodId,
                    activeBuildCountStart: selectedActiveBuildCount,
                    activeBuildCountEnd: selectedActiveBuildCount
                  });
                }
              }}
              setPage={setPage}
              hasAppliedFilters={hasAppliedFilters}
              setHasAppliedFilters={setHasAppliedFilters}

              businessNameSearchValue={businessNameSearchValue}
              setBusinessNameSearchValue={setBusinessNameSearchValue}

              contactNameSearchValue={contactNameSearchValue}
              setContactNameSearchValue={setContactNameSearchValue}

              selectedTerritoryId={selectedTerritoryId}
              setSelectedTerritoryId={setSelectedTerritoryId}

              selectedNeighborhoodId={selectedNeighborhoodId}
              setSelectedNeighborhoodId={setSelectedNeighborhoodId}

              selectedActiveBuildCount={selectedActiveBuildCount}
              setSelectedActiveBuildCount={setSelectedActiveBuildCount}
            />
            <Table
              columns={columns}
              data={framers}
              isLoading={isLoadingFramers}
              hidePagination
              containerClasses='framers-table'
              ResponsiveComponent={isMobile ? MobileTable : undefined}
              mobileProps={{
                fields: columns
                  .filter(col => col.Header)
                  .map(col => ({
                    name: col.Header,
                    accessor: col.accessor,
                    Cell: col?.Cell,
                  })),
                handleEdit: (selectedFramer: IFramerDetail) => {
                  handleEditFramer(selectedFramer);
                },
                handleDelete: (selectedFramer: IFramerDetail) => {
                  handleFramerDelete(selectedFramer.framerId);
                }
              }}
            />
            {!isLoadingFramers && <Pagination page={page} count={recordCount} rowsPerPage={perPage} setPage={setPage} setRowsPerPage={setRowsPerPage} />}
          </Grid>
        </Grid>
      </Page>
      <FramerModal open={isModalOpen} modalIsLoading={isModalLoading} onClose={() => handleModalClose()} currentFramer={selectedFramer} fetchFramers={fetchFramers} />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  link: {
    textDecoration: 'none'
  },
  addFramerButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  tableWrapper: {
    '@media (min-width: 960px)': {
      '& .framers-table': {
        '& .MuiTableCell-root:first-child':{
          minWidth: '200px'
        },
        '& .MuiTableCell-root': {
          maxWidth: '275px'
        }
      },
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
